import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { STATUS_COLORS } from "@/lib/constants";
import { ArrowUpRight, Gauge, CheckCircle2, Clock, Lock } from "lucide-react";
import { useRole } from "@/lib/roles";
import AnalyticsPanel from "@/components/AnalyticsPanel";

const KPI_CONFIG = [
  { key: "Pending", icon: Clock, accent: "text-[#ccb795]" },
  { key: "Tested", icon: CheckCircle2, accent: "text-[#39678f]" },
  { key: "Complete", icon: Lock, accent: "text-[#33424f]" },
];

export default function Dashboard() {
  const { canWrite } = useRole();
  const [data, setData] = useState({ total: 0, counts: {}, recent: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/stats")
      .then((res) => setData(res.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="p-4 md:p-10 max-w-7xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
        <div>
          <div className="eyebrow mb-2">Control Room</div>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl tracking-tight text-[#33424f] leading-none">
            PRESSURE TEST OVERVIEW
          </h1>
          <p className="font-body text-sm text-[#50493b] mt-3">
            Live snapshot of vessel tests in the registry.
          </p>
        </div>
        <Link
          to="/tests/new"
          data-testid="dashboard-new-test-btn"
          className={`hidden md:inline-flex items-center gap-2 bg-[#39678f] hover:bg-[#2c5272] text-white px-4 py-2.5 text-xs uppercase tracking-wider subtitle ${canWrite ? "" : "!hidden"}`}
        >
          Log New Test <ArrowUpRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#33424f]/20 bg-white mb-10">        {KPI_CONFIG.map(({ key, icon: Icon, accent }, i) => (
          <div
            key={key}
            data-testid={`kpi-${key.toLowerCase().replace(" ", "-")}`}
            className={`p-6 ${i < KPI_CONFIG.length - 1 ? "border-b md:border-b-0 md:border-r border-[#33424f]/20" : ""}`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="subtitle text-[10px] uppercase tracking-[0.25em] text-[#50493b]">
                {key}
              </div>
              <Icon className={`w-4 h-4 ${accent}`} />
            </div>
            <div className="font-display text-6xl tabular-nums leading-none text-[#33424f]">
              {loading ? "—" : data.counts[key] || 0}
            </div>
            <div className="mt-4 h-1 w-12">
              <div className={STATUS_COLORS[key].dot + " h-full"} />
            </div>
          </div>
        ))}
      </div>

      {/* Analytics */}
      <div className="mb-10">
        <AnalyticsPanel />
      </div>

      {/* Recent tests */}
      <div className="border border-[#33424f]/20 bg-white">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#33424f]/20">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#50493b] subtitle">
              Recent Activity
            </div>
            <h2 className="font-display text-2xl tracking-tight text-[#33424f]">Latest pressure tests</h2>
          </div>
          <Link
            to="/tests"
            data-testid="view-all-tests-link"
            className="text-xs uppercase tracking-wider text-[#39678f] subtitle hover:underline"
          >
            View all →
          </Link>
        </div>
        {loading ? (
          <div className="p-10 text-center text-sm text-[#50493b]">Loading…</div>
        ) : data.recent.length === 0 ? (
          <div className="p-12 text-center">
            <Gauge className="w-8 h-8 text-[#ccb795] mx-auto mb-3" />
            <div className="text-sm text-[#50493b] mb-1">No tests logged yet</div>
            <Link
              to="/tests/new"
              className="text-xs uppercase tracking-wider text-[#39678f] subtitle hover:underline"
            >
              Log your first test
            </Link>
          </div>
        ) : (
          <>
            <table className="hidden xl:table w-full data-table text-sm">
              <thead>
                <tr>
                  <th className="text-left px-6 py-3">Job</th>
                  <th className="text-left px-6 py-3">Vessel</th>
                  <th className="text-left px-6 py-3">Date</th>
                  <th className="text-left px-6 py-3">Pressure</th>
                  <th className="text-left px-6 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.recent.map((r) => (
                  <tr
                    key={r.id}
                    className="border-t border-[#33424f]/10 hover:bg-[#f5f1ec] transition-colors"
                  >
                    <td className="px-6 py-3 font-semibold">{r.job_number}</td>
                    <td className="px-6 py-3">
                      {r.vessel_type}
                      {r.vessel_number ? ` #${r.vessel_number}` : ""}
                    </td>
                    <td className="px-6 py-3 text-[#50493b]">
                      {r.test_date || "—"}
                    </td>
                    <td className="px-6 py-3">{r.test_pressure || "—"}</td>
                    <td className="px-6 py-3">
                      <StatusBadge status={r.status} size="sm" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile cards */}
            <div className="xl:hidden divide-y divide-[#33424f]/10" data-testid="dashboard-recent-cards">
              {data.recent.map((r) => (
                <div key={r.id} className="p-4">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] subtitle uppercase tracking-[0.2em] text-[#50493b] mb-0.5">Job</div>
                      <div className="font-semibold text-[#33424f] truncate">{r.job_number}</div>
                    </div>
                    <StatusBadge status={r.status} size="sm" />
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm font-body">
                    <div>
                      <div className="text-[10px] subtitle uppercase tracking-[0.2em] text-[#50493b] mb-0.5">Vessel</div>
                      <div className="text-[#33424f] truncate">{r.vessel_type}{r.vessel_number ? ` #${r.vessel_number}` : ""}</div>
                    </div>
                    <div>
                      <div className="text-[10px] subtitle uppercase tracking-[0.2em] text-[#50493b] mb-0.5">Pressure</div>
                      <div className="text-[#33424f] truncate">{r.test_pressure || "—"}</div>
                    </div>
                    <div className="col-span-2">
                      <div className="text-[10px] subtitle uppercase tracking-[0.2em] text-[#50493b] mb-0.5">Date</div>
                      <div className="text-[#50493b]">{r.test_date || "—"}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
